/* ============================================================================
 * useUndoRedo.ts - Undo/redo system with authoritative operation history
 * ============================================================================ */

import { useCallback, useEffect, type Dispatch, type SetStateAction } from 'react';
import { undoStack } from '@/shared/commands/UndoStack';
import { noteAutosaveCoordinator } from '@/features/tasks/services/noteAutosave';

interface UndoState {
  canUndo: boolean;
  canRedo: boolean;
  position: number;
  stackSize: number;
}

interface UseUndoRedoParams {
  setError: Dispatch<SetStateAction<string | null>>;
  setIsUndoRedoInProgress: Dispatch<SetStateAction<boolean>>;
  setUndoState: Dispatch<SetStateAction<UndoState>>;
}

/**
 * Hook for managing undo/redo functionality
 */
export function useUndoRedo({
  setError,
  setIsUndoRedoInProgress,
  setUndoState
}: UseUndoRedoParams) {
  const updateUndoStateFromSnapshots = useCallback((): void => {
    const stackState = undoStack.getState();

    setUndoState(prevState => {
      const newState = {
        canUndo: stackState.canUndo,
        canRedo: stackState.canRedo,
        position: stackState.position,
        stackSize: stackState.entries.length
      };

      if (
        prevState.canUndo !== newState.canUndo ||
        prevState.canRedo !== newState.canRedo ||
        prevState.position !== newState.position ||
        prevState.stackSize !== newState.stackSize
      ) {
        return newState;
      }

      return prevState;
    });
  }, [setUndoState]);

  useEffect(() => {
    updateUndoStateFromSnapshots();
    return undoStack.subscribe(() => {
      updateUndoStateFromSnapshots();
    });
  }, [updateUndoStateFromSnapshots]);

  const undo = useCallback(async () => {
    try {
      setIsUndoRedoInProgress(true);

      await noteAutosaveCoordinator.withHistory(() => undoStack.undo());
    } catch (error) {
      setError(`Undo failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      updateUndoStateFromSnapshots();
      setIsUndoRedoInProgress(false);
    }
  }, [
    setIsUndoRedoInProgress,
    setError,
      updateUndoStateFromSnapshots
  ]);

  const redo = useCallback(async () => {
    try {
      setIsUndoRedoInProgress(true);

      await noteAutosaveCoordinator.withHistory(() => undoStack.redo());
    } catch (error) {
      setError(`Redo failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      updateUndoStateFromSnapshots();
      setIsUndoRedoInProgress(false);
    }
  }, [
    setIsUndoRedoInProgress,
    setError,
      updateUndoStateFromSnapshots
  ]);

  const jumpToHistoryPosition = useCallback(async (position: number) => {
    try {
      setIsUndoRedoInProgress(true);

      await noteAutosaveCoordinator.withHistory(async () => {
        const stackState = undoStack.getState();
        const targetPosition = Math.max(-1, Math.min(position, stackState.entries.length - 1));
        const startPosition = stackState.position;

        if (targetPosition === startPosition) {
          return;
        }

        if (targetPosition < startPosition) {
          while (undoStack.getState().position > targetPosition) {
            const undoneEntry = await undoStack.undo();
            if (!undoneEntry) {
              break;
            }
          }
        } else {
          while (undoStack.getState().position < targetPosition) {
            const redoneEntry = await undoStack.redo();
            if (!redoneEntry) {
              break;
            }
          }
        }

      });
    } catch (error) {
      setError(`Jump to position failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      updateUndoStateFromSnapshots();
      setIsUndoRedoInProgress(false);
    }
  }, [
    setError,
      setIsUndoRedoInProgress,
    updateUndoStateFromSnapshots
  ]);

  const getUndoHistoryList = useCallback(() => {
    return undoStack.getUndoList().map((entry) => ({
      name: entry.name || 'Unknown operation',
      timestamp: entry.timestamp,
      category: entry.category,
      isCurrentPosition: entry.isCurrentPosition,
      canJumpTo: entry.canJumpTo,
      metadata: entry.metadata
    }));
  }, []);

  return {
    updateUndoStateFromSnapshots,
    undo,
    redo,
    jumpToHistoryPosition,
    getUndoHistoryList
  };
}
